import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ActionMenu,
  ActionMenuProps,
  IconButton,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { useMemo } from 'react';
import { IconHandle } from './IconHandle';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

export type TActionType = 'top' | 'up' | 'down' | 'bottom';

export const ACTION_TYPE = {
  TOP: 'top',
  UP: 'up',
  DOWN: 'down',
  BOTTOM: 'bottom',
} as const;

export const DragHandle: React.FC<{
  id: string;
  index: number;
  lastIndex: number;
  setShownMenuId: (id: string | null) => void;
  shownMenuId: string | null | number;
  onClickAction: (action: TActionType, id: string) => void;
}> = ({ id, index, lastIndex, setShownMenuId, shownMenuId, onClickAction }) => {
  const { t } = useTranslation();

  const memorizedButton = useMemo(() => {
    const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (id === shownMenuId) {
        setShownMenuId(null);
      } else {
        setShownMenuId(id);
      }
    };
    return (
      <IconButton
        className={cx(styles['drag-handle-button'])}
        onClick={onClick}
        size='sm'
      >
        <IconHandle />
      </IconButton>
    );
  }, [id, setShownMenuId, shownMenuId]);

  const actionMenuItems = useMemo(() => {
    const isFirst = index === 0;
    const isLast = index === lastIndex;

    return [
      {
        key: ACTION_TYPE.TOP,
        label: t(`draggable_table.drag_handle.${ACTION_TYPE.TOP}`),
        disabled: isFirst,
        onClick: () => !isFirst && onClickAction(ACTION_TYPE.TOP, id),
      },
      {
        key: ACTION_TYPE.UP,
        label: t(`draggable_table.drag_handle.${ACTION_TYPE.UP}`),
        disabled: isFirst,
        onClick: () => !isFirst && onClickAction(ACTION_TYPE.UP, id),
      },
      {
        key: ACTION_TYPE.DOWN,
        label: t(`draggable_table.drag_handle.${ACTION_TYPE.DOWN}`),
        disabled: isLast,
        onClick: () => !isLast && onClickAction(ACTION_TYPE.DOWN, id),
      },
      {
        key: ACTION_TYPE.BOTTOM,
        label: t(`draggable_table.drag_handle.${ACTION_TYPE.BOTTOM}`),
        disabled: isLast,
        onClick: () => !isLast && onClickAction(ACTION_TYPE.BOTTOM, id),
      },
    ];
  }, [id, index, lastIndex, onClickAction, t]);

  const isOpen = useMemo(() => id === shownMenuId, [id, shownMenuId]);

  const dropDownProps: ActionMenuProps['dropDownProps'] = useMemo(
    () => ({
      open: isOpen,
      placement: 'bottomLeft',
    }),
    [isOpen]
  );
  const container = useGlobalContainerRef();

  return (
    <div className={cx([styles['drag-handle-button-container']])}>
      <ActionMenu
        type='custom'
        className={cx([styles['drag-handle-action-menu']])}
        dropDownProps={dropDownProps}
        items={actionMenuItems}
        getPopupContainer={container}
      >
        {memorizedButton}
      </ActionMenu>
    </div>
  );
};
